import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Video } from 'lucide-react';
import { BOOKING_AREAS, BOOKING_CONSENT_WORDING } from '@/data/contact';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
  'mt-1.5 h-11 w-full rounded-md border border-slate-300 bg-white px-3.5 text-sm text-navy-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1';

const FieldError = ({ errors, id }) =>
  errors[id] ? (
    <p role="alert" className="mt-1.5 text-xs font-semibold text-red-700" data-testid={`bk-error-${id}`}>
      {errors[id]}
    </p>
  ) : null;

// Step 3 — contact and business details. Concise by design; privacy
// acknowledgement covers arranging/managing the appointment only.
export const DetailsStep = ({ details, onChange, onBack, onNext }) => {
  const [errors, setErrors] = useState({});

  const set = (key) => (e) => {
    const value = key === 'consent' ? e.target.checked : e.target.value;
    onChange({ ...details, [key]: value });
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (details.name.trim().length < 2) er.name = 'Please enter your name.';
    if (details.businessName.trim().length < 2) er.businessName = 'Please enter your business name.';
    if (!EMAIL_RE.test(details.email.trim())) er.email = 'Please enter a valid email address.';
    if (!details.areaOfInterest) er.areaOfInterest = 'Please choose an area of interest.';
    if (!details.consent)
      er.consent = 'Please confirm you are happy for Amden Group to arrange your appointment.';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  return (
    <div data-testid="booking-step-details">
      <h3 className="text-xl font-extrabold tracking-tight text-navy-900">Your details</h3>
      <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-mist px-4 py-2 text-xs font-bold text-navy-800">
        <Video className="h-3.5 w-3.5" aria-hidden="true" />
        Meeting format: Online (video call)
      </div>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="bk-name" className="text-sm font-bold text-navy-900">
            Name <span className="text-gold-700" aria-hidden="true">*</span>
          </label>
          <input id="bk-name" type="text" autoComplete="name" value={details.name} onChange={set('name')} data-testid="bk-name" className={inputCls} />
          <FieldError errors={errors} id="name" />
        </div>
        <div>
          <label htmlFor="bk-business" className="text-sm font-bold text-navy-900">
            Business name <span className="text-gold-700" aria-hidden="true">*</span>
          </label>
          <input id="bk-business" type="text" autoComplete="organization" value={details.businessName} onChange={set('businessName')} data-testid="bk-business-name" className={inputCls} />
          <FieldError errors={errors} id="businessName" />
        </div>
        <div>
          <label htmlFor="bk-email" className="text-sm font-bold text-navy-900">
            Email address <span className="text-gold-700" aria-hidden="true">*</span>
          </label>
          <input id="bk-email" type="email" autoComplete="email" value={details.email} onChange={set('email')} data-testid="bk-email" className={inputCls} />
          <FieldError errors={errors} id="email" />
        </div>
        <div>
          <label htmlFor="bk-phone" className="text-sm font-bold text-navy-900">
            Phone number
          </label>
          <input id="bk-phone" type="tel" autoComplete="tel" value={details.phone} onChange={set('phone')} data-testid="bk-phone" className={inputCls} />
        </div>
        <div>
          <label htmlFor="bk-location" className="text-sm font-bold text-navy-900">
            Location
          </label>
          <input id="bk-location" type="text" placeholder="Town/County" value={details.location} onChange={set('location')} data-testid="bk-location" className={inputCls} />
        </div>
        <div>
          <label htmlFor="bk-area" className="text-sm font-bold text-navy-900">
            Area of interest <span className="text-gold-700" aria-hidden="true">*</span>
          </label>
          <select id="bk-area" value={details.areaOfInterest} onChange={set('areaOfInterest')} data-testid="bk-area" className={inputCls}>
            <option value="">Select an area…</option>
            {BOOKING_AREAS.map((a) => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
          <FieldError errors={errors} id="areaOfInterest" />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="bk-context" className="text-sm font-bold text-navy-900">
            Brief meeting context <span className="font-medium text-slate-400">(optional)</span>
          </label>
          <textarea
            id="bk-context"
            rows={4}
            value={details.context}
            onChange={set('context')}
            placeholder="Anything useful for us to know before the conversation…"
            data-testid="bk-context"
            className="mt-1.5 w-full rounded-md border border-slate-300 bg-white px-3.5 py-3 text-sm text-navy-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
          />
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3">
        <input
          id="bk-consent"
          type="checkbox"
          checked={details.consent}
          onChange={set('consent')}
          data-testid="bk-consent"
          className="mt-1 h-4 w-4 rounded border-slate-300 text-navy-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
        />
        <label htmlFor="bk-consent" className="text-sm leading-relaxed text-slate-600">
          {BOOKING_CONSENT_WORDING}{' '}
          <Link to="/privacy-policy" className="font-bold text-navy-900 underline-offset-2 hover:underline">
            Privacy Policy
          </Link>
          .
        </label>
      </div>
      <FieldError errors={errors} id="consent" />

      <div className="mt-8 flex items-center justify-between">
        <button type="button" onClick={onBack} data-testid="booking-back-details" className="inline-flex items-center gap-2 text-sm font-bold text-navy-900 underline-offset-2 hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back
        </button>
        <button
          type="button"
          onClick={() => validate() && onNext()}
          data-testid="booking-next-details"
          className="group inline-flex items-center gap-2 rounded-md bg-navy-900 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-navy-700"
        >
          Review Booking
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};
